import { useState } from 'react';

type returnTypes<Type> = [
  boolean,
  () => void,
  () => void,
  () => void,
  (value: Type) => void,
]

/**
 * Provides functions for working with a boolean state
 * @returns In order:
 * - 0: The boolean state value
 * - 1: A function that changes the state value to the opposite boolean value
 * - 2: A function that changes the state value to false
 * - 3: A function that changes the state value to true
 * - 4: A function that sets the state to the boolean value of any value
 */
function useToggleState<Type>(initialState: Type): returnTypes<Type> {
  const [state, setState] = useState<boolean>(Boolean(initialState));

  /** Changes the state value to the opposite boolean value */
  const toggleState = () => setState((o) => !o);

  /** Changes the state value to false */
  const setFalse = () => setState(false);

  /** Changes the state value to true */
  const setTrue = () => setState(true);

  /** Sets the state to the boolean value of any value */
  const setBooleanState = (value: Type) => setState(Boolean(value));

  return [
    state, toggleState, setFalse, setTrue, setBooleanState,
  ];
}

export default useToggleState;
